import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import clsx from 'clsx';
import { DuelStatus, IDuel, IDuelPerformance } from '@pvplevelup/core/dist/interfaces/IDuel';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';
import { TeamColors } from 'utilities/team_colors';
import { DuelRoundBreakdown, formatDuration } from './DuelRoundBreakdown';

const MUTED = 'rgba(255, 255, 255, 0.6)';
const DIVIDER = '1px solid rgba(255, 255, 255, 0.07)';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles(theme => ({
  hero: {
    position: 'relative',
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(28, 29, 35, 0.97), rgba(17, 17, 21, 0.97))',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.35)',
  },
  heroTop: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.25, 2.25),
    borderBottom: DIVIDER,
    background: 'rgba(255, 255, 255, 0.02)',
  },
  kicker: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
    justifyContent: 'start',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  '@keyframes livePulse': {
    '0%': {boxShadow: '0 0 0 0 rgba(239, 83, 80, 0.7)'},
    '70%': {boxShadow: '0 0 0 7px rgba(239, 83, 80, 0)'},
    '100%': {boxShadow: '0 0 0 0 rgba(239, 83, 80, 0)'},
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#ef5350',
    animation: '$livePulse 1.6s infinite',
  },
  actions: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: theme.spacing(1),
    alignItems: 'center',
  },
  matchup: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    padding: theme.spacing(2.5, 2.25, 2),
  },
  side: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 14,
    alignItems: 'center',
  },
  sideRight: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    textAlign: 'right',
  },
  classTile: {
    width: 64,
    height: 64,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    '& img': {
      width: 48,
      height: 48,
      display: 'block',
    },
  },
  name: {
    fontSize: '1.35rem',
    fontWeight: 800,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  playerMeta: {
    marginTop: 3,
    fontSize: '0.82rem',
    color: MUTED,
  },
  winnerTag: {
    display: 'inline-block',
    marginTop: 6,
    padding: '1px 8px',
    borderRadius: 8,
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#ffd54f',
    background: 'rgba(255, 213, 79, 0.12)',
    border: '1px solid rgba(255, 213, 79, 0.35)',
  },
  center: {
    padding: theme.spacing(0, 3),
    textAlign: 'center',
  },
  score: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 14,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2.6rem',
    fontWeight: 900,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  scoreDash: {
    fontSize: '1.6rem',
    color: 'rgba(255, 255, 255, 0.3)',
  },
  scoreLead: {
    textShadow: '0 0 18px rgba(255, 255, 255, 0.35)',
  },
  scoreTrail: {
    color: 'rgba(255, 255, 255, 0.45)',
  },
  bestOf: {
    marginTop: 6,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: MUTED,
  },
  pills: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    justifyContent: 'center',
    padding: theme.spacing(0, 2.25, 2),
  },
  pill: {
    padding: '3px 10px',
    borderRadius: 10,
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  pillRanked: {
    color: '#90caf9',
    background: 'rgba(66, 165, 245, 0.14)',
    borderColor: 'rgba(66, 165, 245, 0.35)',
  },
  waiting: {
    padding: theme.spacing(3, 2),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    textAlign: 'center',
    fontSize: '0.9rem',
    color: MUTED,
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px dashed rgba(255, 255, 255, 0.12)',
  },
}));

const MmrChange: React.FC<{value?: number | null}> = ({value}) => {
  if(value == null)
    return null;
  let color = value > 0 ? '#66bb6a' : value < 0 ? '#ef5350' : '#bdbdbd';
  return <span style={{color, fontWeight: 700}}>{value > 0 ? '+' : ''}{value} MMR</span>;
}

const idOf = (value: any) => value == null ? null : String(value._id ?? value);

const Player: React.FC<{performance?: IDuelPerformance, winner: boolean, color: string, right?: boolean}> = ({performance, winner, color, right}) => {
  const classes = useStyles();
  if(!performance)
    return <div />;
  const icon = (
    <div className={classes.classTile} style={{boxShadow: `inset 0 -3px 0 ${color}`}}>
      <img src={classImages.getColorIcon(performance.class as AocClasses)} title={performance.class} />
    </div>
  );
  const text = (
    <div style={{minWidth: 0}}>
      <div className={classes.name} style={winner ? {color: '#fff'} : undefined}>{performance.name}</div>
      <div className={classes.playerMeta}>
        {performance.class}{performance.mmrChange != null ? ' · ' : ''}<MmrChange value={performance.mmrChange} />
      </div>
      {winner && <span className={classes.winnerTag}>Winner</span>}
    </div>
  );
  return <div className={clsx(classes.side, {[classes.sideRight]: right})}>{right ? <React.Fragment>{text}{icon}</React.Fragment> : <React.Fragment>{icon}{text}</React.Fragment>}</div>;
}

interface Props {
  duel: IDuel;
  /** small heading over the matchup, e.g. "Last duel" */
  kicker?: string;
  /** buttons on the right of the heading */
  actions?: React.ReactNode;
}

/** A duel's matchup, score and round by round breakdown; live while the duel is still being fought. */
export const DuelBreakdown: React.FC<Props> = ({duel, kicker, actions}) => {
  const classes = useStyles();
  const performances = duel.performances || [];
  const playerOrder = performances.map((p) => p.name || "");
  const rounds = [...(duel.rounds || [])].sort((a, b) => (a.round || 0) - (b.round || 0));
  const live = duel.status == DuelStatus.Active || duel.status == DuelStatus.Starting || duel.status == DuelStatus.Pending || duel.status == DuelStatus.Paused;
  const winnerId = idOf(duel.winner);
  const isWinner = (p?: IDuelPerformance) => !live && p != null && winnerId != null && idOf(p.character) == winnerId;
  const one = performances[0]?.roundsWon || 0;
  const two = performances[1]?.roundsWon || 0;

  const liveLabel = duel.status == DuelStatus.Active ? `Round ${duel.round || rounds.length + 1} in progress`
    : duel.status == DuelStatus.Paused ? 'Paused'
    : `Round ${duel.round || rounds.length + 1} starting`;

  return (
    <React.Fragment>
      <div className={classes.hero}>
        <div className={classes.heroTop}>
          <div className={classes.kicker}>
            {live && <span className={classes.liveDot} />}
            <span>{kicker || (live ? 'Live duel' : 'Duel')}{live ? ` · ${liveLabel}` : ''}</span>
          </div>
          <div className={classes.actions}>
            {actions}
          </div>
        </div>
        <div className={classes.matchup}>
          <Player performance={performances[0]} winner={isWinner(performances[0])} color={TeamColors[0]} />
          <div className={classes.center}>
            <div className={classes.score}>
              <span className={one > two ? classes.scoreLead : one < two ? classes.scoreTrail : undefined}>{one}</span>
              <span className={classes.scoreDash}>–</span>
              <span className={two > one ? classes.scoreLead : two < one ? classes.scoreTrail : undefined}>{two}</span>
            </div>
            <div className={classes.bestOf}>Best of {duel.bestOf || 1}</div>
          </div>
          <Player performance={performances[1]} winner={isWinner(performances[1])} color={TeamColors[1]} right />
        </div>
        <div className={classes.pills}>
          <span className={clsx(classes.pill, {[classes.pillRanked]: duel.ranked})}>{duel.ranked ? 'Ranked' : 'Unranked'}</span>
          {duel.server && <span className={classes.pill}>{duel.server}</span>}
          {(duel.start_time || duel.createdAt) && <span className={classes.pill}>{moment(duel.start_time || duel.createdAt).format('MMM Do YYYY, h:mm a')}</span>}
          {duel.timeElapsed != null && duel.timeElapsed > 0 && <span className={classes.pill}>Fight time {formatDuration(duel.timeElapsed)}</span>}
          {!live && duel.status != DuelStatus.Completed && duel.status && <span className={classes.pill}>{duel.status}</span>}
        </div>
      </div>

      {rounds.map((round) => <DuelRoundBreakdown key={round.round} round={round} playerOrder={playerOrder} />)}
      {live ?
        <div className={classes.waiting}>{rounds.length == 0 ? 'The breakdown of each round appears here as soon as it ends.' : 'The next round appears here when it ends.'}</div>
      : rounds.length == 0 &&
        <div className={classes.waiting}>No rounds were recorded for this duel.</div>
      }
    </React.Fragment>
  );
}
